import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView, Platform } from "react-native";
import { Checkbox } from "expo-checkbox";
import { PdfCanvas } from "@/components/Pdf/PdfCanvas";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { invoiceService } from "@/services/invoiceService";
import { supabase } from "@/lib/supabase-client";

interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string; // Storage path (not public URL)
  bucket?: string; // Bucket name for signed URL generation
  uploadedAt: string;
  uploadedBy: string;
}

interface FileDisplayOptions {
  fit: "contain" | "cover" | "fill";
  align: "left" | "center" | "right";
  valign: "top" | "center" | "bottom";
}

interface PdfModalProps {
  src: string;
  onClose: () => void;
  onRegenerate?: (fileOptions: Map<string, FileDisplayOptions & { url: string; name: string }>) => void;
  entityId?: string;
  entityType?: "invoice";
  invoiceNumber?: string;
  projectName?: string;
  invoiceDate?: string;
  amountDue?: string;
  loading?: boolean;
  dirty?: boolean;
}

export function PdfModal({
  src,
  onClose,
  onRegenerate,
  entityId,
  entityType,
  invoiceNumber,
  projectName,
  invoiceDate,
  amountDue,
  loading = false,
  dirty = false,
}: PdfModalProps) {
  const { colors } = useTheme();
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [fileOptions, setFileOptions] = useState<Map<string, FileDisplayOptions>>(new Map());
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update currentSrc when src prop changes
  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  // Load files on mount
  useEffect(() => {
    if (entityId && entityType === "invoice") {
      loadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  // Trigger PDF regeneration when selected files or options change
  // Skip on initial mount
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only trigger if we have selected files
    if (selectedFiles.size > 0) {
      triggerRegeneration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, fileOptions]);

  const loadFiles = async () => {
    if (!entityId) return;

    try {
      setLoadingFiles(true);
      const fileMetadata = await invoiceService.getFiles(entityId);
      setFiles(fileMetadata);
    } catch (err) {
      console.error("Failed to load files:", err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const toggleFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
        // Remove options when unchecking
        setFileOptions((prevOpts) => {
          const newOpts = new Map(prevOpts);
          newOpts.delete(fileId);
          return newOpts;
        });
      } else {
        next.add(fileId);
        // Initialize default options when checking
        setFileOptions((prevOpts) => {
          const newOpts = new Map(prevOpts);
          newOpts.set(fileId, { fit: "contain", align: "center", valign: "center" });
          return newOpts;
        });
      }
      return next;
    });
    // Regeneration will be triggered by useEffect watching selectedFiles/fileOptions
  }, []);

  const triggerRegeneration = useCallback(async () => {
    setIsRegenerating(true);

    // Call onRegenerate callback if provided
    if (onRegenerate) {
      try {
        // Generate signed URLs for all selected files
        const fileOptionsWithMetadata = new Map<string, FileDisplayOptions & { url: string; name: string }>();

        for (const fileId of Array.from(selectedFiles)) {
          const file = files.find((f) => f.id === fileId);
          const options = fileOptions.get(fileId);

          if (file && options) {
            // Generate signed URL for this file
            const bucket = file.bucket || "invoices"; // Default to invoices bucket
            const path = file.url; // This is the storage path

            const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600); // 1 hour expiry

            if (error) {
              console.error(`Failed to generate signed URL for ${file.name}:`, error);
              continue;
            }

            if (data?.signedUrl) {
              fileOptionsWithMetadata.set(fileId, {
                ...options,
                url: data.signedUrl, // Use signed URL instead of storage path
                name: file.name,
              });
            }
          }
        }

        onRegenerate(fileOptionsWithMetadata);
      } catch (error) {
        console.error("Error generating signed URLs:", error);
      }
    }

    setTimeout(() => setIsRegenerating(false), 1000);
  }, [selectedFiles, fileOptions, files, onRegenerate]);

  const updateFileOption = useCallback((fileId: string, key: keyof FileDisplayOptions, value: string) => {
    setFileOptions((prev) => {
      const newOpts = new Map(prev);
      const current = newOpts.get(fileId) || { fit: "contain", align: "center", valign: "center" };
      newOpts.set(fileId, { ...current, [key]: value });
      return newOpts;
    });
    // Regeneration will be triggered by useEffect watching fileOptions
  }, []);

  const toggleAllFiles = useCallback(() => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
      setFileOptions(new Map());
    } else {
      const allFileIds = files.map((f) => f.id);
      setSelectedFiles(new Set(allFileIds));
      // Initialize default options for all files
      const newOpts = new Map<string, FileDisplayOptions>();
      allFileIds.forEach((id) => {
        newOpts.set(id, { fit: "contain", align: "center", valign: "center" });
      });
      setFileOptions(newOpts);
    }
    // Regeneration will be triggered by useEffect watching selectedFiles/fileOptions
  }, [files, selectedFiles]);

  const handleDownload = () => {
    // Filename: <invoiceNumber>_<projectName>_<invoiceDate>.pdf — underscore-separated,
    // each segment sanitized so spaces/slashes/commas don't corrupt the name. Empty
    // segments are dropped; falls back to "invoice" if all are missing.
    const sanitize = (s?: string) =>
      (s ?? "")
        .toString()
        .trim()
        .replace(/[^A-Za-z0-9.-]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const invoicePart = invoiceNumber ? `INV${invoiceNumber}` : "";
    const parts = [invoicePart, projectName, invoiceDate].map(sanitize).filter(Boolean);
    const fileName = `${parts.length ? parts.join("_") : "invoice"}.pdf`;

    // Create download link using the data URL
    const link = document.createElement("a");
    link.href = currentSrc;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <View style={[styles.container, { width: dimensions.width * 0.8, height: dimensions.height * 0.89 }]}>
      <View style={styles.header}>
        <ThemedText type="title" style={[styles.title, { color: colors.text }]}>
          Invoice PDF Preview
        </ThemedText>
      </View>
      {/* Body */}
      <View style={{ flexDirection: "row", gap: 12, flex: 1, minHeight: 0 }}>
        {/* right side PDF Preview Area */}
        <View style={{ flex: 1, minWidth: 0 }}>
          <PdfCanvas src={currentSrc} loading={isRegenerating || loading} dirty={dirty} />
        </View>
        {/* left side controls */}
        <View style={{ width: 300, gap: 16 }}>
          {/* Summary */}
          {(invoiceNumber || invoiceDate || amountDue) && (
            <View style={[styles.summary, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
              {invoiceNumber && (
                <ThemedText style={[styles.summaryText, { color: colors.text, fontWeight: "600" }]}>
                  Invoice #{invoiceNumber}
                </ThemedText>
              )}
              {invoiceDate && (
                <ThemedText style={[styles.summaryText, { color: colors.textSecondary, fontSize: 12 }]}>
                  {invoiceDate}
                </ThemedText>
              )}
              {amountDue && (
                <ThemedText style={[styles.summaryText, { color: colors.text, fontWeight: "600", marginTop: 4 }]}>
                  {amountDue}
                </ThemedText>
              )}
            </View>
          )}

          {/* File Attachments Section */}
          {files.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>
                  Attachments ({selectedFiles.size}/{files.length})
                </ThemedText>
                <Button
                  text={selectedFiles.size === files.length ? "None" : "All"}
                  onPress={toggleAllFiles}
                  style={[
                    styles.toggleButton,
                    { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
                  ]}
                  textStyle={{ fontSize: 11 }}
                />
              </View>

              <ScrollView style={styles.fileList} showsVerticalScrollIndicator={false}>
                {files.map((file) => {
                  const isSelected = selectedFiles.has(file.id);
                  const options = fileOptions.get(file.id);
                  return (
                    <View key={file.id} style={styles.fileItem}>
                      <View style={styles.checkboxRow}>
                        {Platform.OS === "web" ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleFile(file.id)}
                            style={{ marginRight: 8 }}
                          />
                        ) : (
                          <Checkbox
                            style={styles.checkbox}
                            value={isSelected}
                            onValueChange={() => toggleFile(file.id)}
                          />
                        )}
                        <ThemedText style={[styles.fileLabel, { color: colors.text }]} numberOfLines={2}>
                          {file.name}
                        </ThemedText>
                      </View>

                      {/* Display options - only show when file is selected */}
                      {isSelected && options && (
                        <View style={[styles.optionsContainer, { backgroundColor: colors.backgroundSecondary }]}>
                          {/* Fit option */}
                          <View style={styles.optionRow}>
                            <ThemedText style={[styles.optionLabel, { color: colors.textSecondary }]}>Fit:</ThemedText>
                            {Platform.OS === "web" ? (
                              <select
                                value={options.fit}
                                onChange={(e) => updateFileOption(file.id, "fit", e.target.value)}
                                style={{
                                  flex: 1,
                                  padding: 4,
                                  borderRadius: 4,
                                  border: `1px solid ${colors.border}`,
                                  backgroundColor: colors.background,
                                  color: colors.text,
                                  fontSize: 12,
                                }}
                              >
                                <option value="contain">Contain</option>
                                <option value="cover">Cover</option>
                                <option value="fill">Fill</option>
                              </select>
                            ) : (
                              <ThemedText style={{ fontSize: 12 }}>{options.fit}</ThemedText>
                            )}
                          </View>

                          {/* Align option */}
                          <View style={styles.optionRow}>
                            <ThemedText style={[styles.optionLabel, { color: colors.textSecondary }]}>
                              Align:
                            </ThemedText>
                            {Platform.OS === "web" ? (
                              <select
                                value={options.align}
                                onChange={(e) => updateFileOption(file.id, "align", e.target.value)}
                                style={{
                                  flex: 1,
                                  padding: 4,
                                  borderRadius: 4,
                                  border: `1px solid ${colors.border}`,
                                  backgroundColor: colors.background,
                                  color: colors.text,
                                  fontSize: 12,
                                }}
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            ) : (
                              <ThemedText style={{ fontSize: 12 }}>{options.align}</ThemedText>
                            )}
                          </View>

                          {/* Valign option */}
                          <View style={styles.optionRow}>
                            <ThemedText style={[styles.optionLabel, { color: colors.textSecondary }]}>
                              Valign:
                            </ThemedText>
                            {Platform.OS === "web" ? (
                              <select
                                value={options.valign}
                                onChange={(e) => updateFileOption(file.id, "valign", e.target.value)}
                                style={{
                                  flex: 1,
                                  padding: 4,
                                  borderRadius: 4,
                                  border: `1px solid ${colors.border}`,
                                  backgroundColor: colors.background,
                                  color: colors.text,
                                  fontSize: 12,
                                }}
                              >
                                <option value="top">Top</option>
                                <option value="center">Center</option>
                                <option value="bottom">Bottom</option>
                              </select>
                            ) : (
                              <ThemedText style={{ fontSize: 12 }}>{options.valign}</ThemedText>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </ScrollView>
            </View>
          )}
          {/* Actions */}
          <View style={styles.actions}>
            <Button
              text="Cancel"
              onPress={onClose}
              style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
            />
            <Button
              text={isRegenerating ? "Regenerating..." : "Download PDF"}
              iconPosition="right"
              icon="download"
              onPress={handleDownload}
              enabled={!isRegenerating && !loading}
              style={[styles.actionButton, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 16,
    display: "flex",
    flexDirection: "column",
  },
  header: {},
  title: {
    fontSize: 18,
    fontWeight: "600",
    textAlign: "center",
  },
  summary: {
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  summaryText: {
    fontSize: 13,
  },
  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "500",
  },
  toggleButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  fileList: {
    maxHeight: 300,
  },
  fileItem: {
    marginBottom: 8,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
  },
  checkbox: {
    marginRight: 8,
  },
  fileLabel: {
    fontSize: 13,
    flex: 1,
  },
  optionsContainer: {
    marginTop: 4,
    marginLeft: 24,
    padding: 8,
    borderRadius: 4,
    gap: 6,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  optionLabel: {
    fontSize: 11,
    width: 50,
  },
  pdfContainer: {
    flex: 1,
    marginBottom: 16,
    borderRadius: 8,
    overflow: "hidden",
  },
  pdfContent: {},
  actions: {
    flex: 1,
    justifyContent: "flex-end",
    gap: 12,
  },
  actionButton: {
    gap: 8,
    minHeight: 44,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 6,
    borderWidth: 1,
  },
});
